'use client';

import { type FormEvent, useEffect, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import DeleteConfirmModal from './DeleteConfirmModal';
import { BaseModal } from './BaseModal';
import { Select } from './Select';
import { cn } from '@/lib/cn';
import { resolveAppHref } from '../lib/portalRoutes';
import { resolveApiErrorToastMessage } from '../lib/api/localizeApiError';
import { useUpdateMyProfileMutation } from '../hooks/queries/useMember';
import { clearClientSessionData, getAuthSession, saveAuthSession, subscribeAuthSession } from '../lib/authSession';
import { useWithdrawMemberMutation } from '../hooks/queries/useMember';
import SessionManagement from './SessionManagement';
import { useAppLocale } from '../providers/AppLocaleProvider';
import { saveStoredLocale, type AppLocale } from '../lib/locale';
import DeployDiagnosticsPanel from './DeployDiagnosticsPanel';
import PersonalNotificationRules from './PersonalNotificationRules';

const localeOptions: { value: AppLocale; labelKey: string }[] = [
  { value: 'en', labelKey: 'languageEn' },
  { value: 'ko', labelKey: 'languageKo' },
];

type ProfileTab = 'account' | 'notifications' | 'sessions' | 'deployDiagnostics';

const baseProfileTabs = ['account', 'notifications', 'sessions'] as const satisfies readonly ProfileTab[];

export const getProfileTabs = (isSystemAdmin: boolean): ProfileTab[] =>
  isSystemAdmin ? [...baseProfileTabs, 'deployDiagnostics'] : [...baseProfileTabs];

type ProfileModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export default function ProfileModal({ isOpen, onClose }: ProfileModalProps) {
  const t = useTranslations('profile.page');
  const tCommon = useTranslations('common');
  const tRoot = useTranslations();
  const [session, setSession] = useState(() => getAuthSession());
  const token = session?.token ?? null;
  const [nickname, setNickname] = useState(() => session?.member.nickname ?? '');
  const [activeProfileTab, setActiveProfileTab] = useState<ProfileTab>('account');
  const [isWithdrawConfirmOpen, setIsWithdrawConfirmOpen] = useState(false);
  const [isWithdrawFinalConfirmOpen, setIsWithdrawFinalConfirmOpen] = useState(false);
  const [withdrawPassword, setWithdrawPassword] = useState('');

  const updateMutation = useUpdateMyProfileMutation(token ?? '');
  const localeMutation = useUpdateMyProfileMutation(token ?? '');
  const betaMutation = useUpdateMyProfileMutation(token ?? '');
  const withdrawMutation = useWithdrawMemberMutation(token ?? '');
  const { locale: currentLocale, setLocale } = useAppLocale();

  useEffect(() => {
    if (isOpen) {
      setActiveProfileTab('account');
      return;
    }

    setIsWithdrawConfirmOpen(false);
    setIsWithdrawFinalConfirmOpen(false);
    setWithdrawPassword('');
  }, [isOpen]);

  const isDirty = useMemo(() => {
    const original = session?.member.nickname ?? '';
    return nickname.trim() !== original;
  }, [nickname, session?.member.nickname]);

  const profileTabs = useMemo(
    () => getProfileTabs(session?.member.isSystemAdmin === true),
    [session?.member.isSystemAdmin],
  );

  useEffect(() => {
    const unsubscribe = subscribeAuthSession((next) => {
      setSession(next);
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!session) {
      return;
    }
    setNickname(session.member.nickname);
  }, [session]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!session || !token) {
      toast.error(t('toastAuthRequired'));
      return;
    }

    const trimmedNickname = nickname.trim();
    if (trimmedNickname.length < 2 || trimmedNickname.length > 20) {
      toast.error(t('toastNicknameRange'));
      return;
    }

    try {
      const updatedMember = await updateMutation.mutateAsync({ nickname: trimmedNickname });
      saveAuthSession({
        ...session,
        member: {
          id: updatedMember.id,
          email: updatedMember.email,
          nickname: updatedMember.nickname,
          tier: updatedMember.tier,
          locale: updatedMember.locale,
          isSystemAdmin: updatedMember.isSystemAdmin,
          isBetaUser: updatedMember.isBetaUser,
          createdAt: updatedMember.createdAt,
          updatedAt: updatedMember.updatedAt,
        },
      });
      toast.success(t('toastSaved'));
      onClose();
    } catch (submitError) {
      const message = resolveApiErrorToastMessage(tRoot, submitError);
      if (message) toast.error(message);
    }
  };

  const handleWithdraw = async () => {
    if (!session || !token) {
      toast.error(t('toastAuthRequired'));
      return;
    }

    try {
      const message = await withdrawMutation.mutateAsync({ password: withdrawPassword });
      toast.success(message || t('withdrawToastSuccess'));
      try {
        const { logoutApi } = await import('@/lib/api/auth');
        await logoutApi(token, session?.refreshToken);
      } catch {
        // 로그아웃 API 실패해도 진행
      }
      // callerHandlesRedirect: 탈퇴 후에는 계정 자체가 없으므로 AuthGuard의 ?redirect= 선점 이동을 막는다.
      await clearClientSessionData({ callerHandlesRedirect: true });
      window.location.href = resolveAppHref('/auth/login');
    } catch (withdrawError) {
      const message = resolveApiErrorToastMessage(tRoot, withdrawError);
      if (message) toast.error(message);
    }
  };

  const handleSelectLocale = async (next: AppLocale) => {
    if (next === currentLocale) return;
    const previous = currentLocale;
    setLocale(next);
    if (!token || !session) {
      return;
    }
    try {
      const updatedMember = await localeMutation.mutateAsync({ locale: next });
      saveAuthSession({
        ...session,
        member: {
          id: updatedMember.id,
          email: updatedMember.email,
          nickname: updatedMember.nickname,
          tier: updatedMember.tier,
          locale: updatedMember.locale,
          isSystemAdmin: updatedMember.isSystemAdmin,
          isBetaUser: updatedMember.isBetaUser,
          createdAt: updatedMember.createdAt,
          updatedAt: updatedMember.updatedAt,
        },
      });
      toast.success(t('languageSaved'));
    } catch (localeError) {
      setLocale(previous);
      saveStoredLocale(previous);
      const message = resolveApiErrorToastMessage(tRoot, localeError) || t('languageSaveFailed');
      toast.error(message);
    }
  };

  const handleToggleBeta = async (nextBeta: boolean) => {
    if (!token || !session) {
      toast.error(t('toastAuthRequired'));
      return;
    }
    try {
      const updatedMember = await betaMutation.mutateAsync({ isBetaUser: nextBeta });
      saveAuthSession({
        ...session,
        member: {
          id: updatedMember.id,
          email: updatedMember.email,
          nickname: updatedMember.nickname,
          tier: updatedMember.tier,
          locale: updatedMember.locale,
          isSystemAdmin: updatedMember.isSystemAdmin,
          isBetaUser: updatedMember.isBetaUser,
          createdAt: updatedMember.createdAt,
          updatedAt: updatedMember.updatedAt,
        },
      });
      toast.success(updatedMember.isBetaUser ? t('betaEnabled') : t('betaDisabled'));
    } catch (betaError) {
      const message = resolveApiErrorToastMessage(tRoot, betaError) || t('betaSaveFailed');
      toast.error(message);
    }
  };

  const handleWithdrawRequest = () => {
    if (withdrawPassword.trim().length === 0) {
      toast.error(t('withdrawPasswordRequired'));
      return;
    }

    setIsWithdrawFinalConfirmOpen(true);
  };

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} variant="bottomSheet" size="md">
      <div className="mb-5 flex items-center justify-between">
        <h3 className="text-base font-semibold">{t('title')}</h3>
        <button type="button" aria-label="Close" className="btn btn-ghost btn-circle btn-sm" onClick={onClose}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* 시스템 관리자만 배포 진단이 붙어 4칸이 된다. Tailwind가 클래스를 정적으로 스캔하므로 문자열을 조립하지 않는다. */}
      <div
        role="tablist"
        aria-label={t('title')}
        className={cn('mb-5 grid rounded-xl bg-base-200 p-1', profileTabs.length === 4 ? 'grid-cols-4' : 'grid-cols-3')}
      >
        {profileTabs.map((tab) => (
          <button
            key={tab}
            id={`profile-tab-${tab}`}
            type="button"
            role="tab"
            aria-selected={activeProfileTab === tab}
            aria-controls={`profile-tabpanel-${tab}`}
            className={cn(
              'btn btn-ghost btn-sm min-h-9 rounded-lg border-0 px-2 text-xs font-medium sm:px-3 sm:text-sm',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
              activeProfileTab === tab && 'bg-base-100 text-primary shadow-xs',
            )}
            onClick={() => setActiveProfileTab(tab)}
          >
            {t(`tabs.${tab}`)}
          </button>
        ))}
      </div>

      {activeProfileTab === 'account' ? (
        <div id="profile-tabpanel-account" role="tabpanel" aria-labelledby="profile-tab-account">
          <form className="flex flex-col gap-4" onSubmit={(e) => void handleSubmit(e)}>
            <div>
              <span className="mb-1 block text-xs font-medium text-[var(--color-fg-subtle)]">{t('email')}</span>
              <input
                type="email"
                className="input input-bordered input-sm w-full"
                value={session?.member.email ?? ''}
                readOnly
              />
            </div>

            <div>
              <span className="mb-1 block text-xs font-medium text-[var(--color-fg-subtle)]">{t('nickname')}</span>
              <input
                type="text"
                className="input input-bordered input-sm w-full"
                value={nickname}
                onChange={(event) => setNickname(event.target.value)}
                required
              />
              <span className="mt-1 block text-xs text-[var(--color-fg-muted)]">{t('nicknameHint')}</span>
            </div>

            <button
              className="btn btn-primary btn-sm mt-1"
              type="submit"
              disabled={!isDirty || updateMutation.isPending}
            >
              {updateMutation.isPending ? t('saving') : tCommon('actions.save')}
            </button>
          </form>

          <div className="mt-6 border-t border-base-300 pt-4">
            <h4 className="text-sm font-semibold">{t('languageTitle')}</h4>
            <p className="mt-1 text-xs text-[var(--color-fg-muted)]">{t('languageDescription')}</p>
            <Select
              size="sm"
              fullWidth
              className="mt-3"
              value={currentLocale}
              onChange={(value) => void handleSelectLocale(value as AppLocale)}
              disabled={localeMutation.isPending}
              options={localeOptions.map((option) => ({
                value: option.value,
                label: t(option.labelKey),
              }))}
            />
          </div>

          <div className="mt-6 border-t border-base-300 pt-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h4 className="text-sm font-semibold">{t('betaTitle')}</h4>
                <p className="mt-1 text-xs text-[var(--color-fg-muted)]">{t('betaDescription')}</p>
              </div>
              <input
                type="checkbox"
                role="switch"
                aria-label={t('betaTitle')}
                className="toggle toggle-primary toggle-sm mt-0.5 shrink-0"
                checked={session?.member.isBetaUser ?? false}
                onChange={(event) => void handleToggleBeta(event.target.checked)}
                disabled={betaMutation.isPending}
              />
            </div>
          </div>

          <div className="mt-6 border-t border-base-300 pt-4">
            {!isWithdrawConfirmOpen ? (
              <div className="flex items-center justify-between gap-3">
                <h4 className="text-sm font-semibold text-[var(--color-fg-subtle)]">{t('withdrawTitle')}</h4>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm text-error/80 hover:text-error"
                  onClick={() => setIsWithdrawConfirmOpen(true)}
                  disabled={withdrawMutation.isPending}
                >
                  {t('withdrawContinue')}
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-error">{t('withdrawTitle')}</h4>
                <p className="text-xs text-[var(--color-fg-muted)]">{t('withdrawWarningDescription')}</p>
                <ul className="list-disc space-y-1 pl-4 text-xs text-[var(--color-fg-muted)]">
                  <li>{t('withdrawWarningAccount')}</li>
                  <li>{t('withdrawWarningMembership')}</li>
                  <li>{t('withdrawWarningOwner')}</li>
                </ul>
                <label className="form-control w-full">
                  <span className="label-text mb-1 text-xs text-[var(--color-fg-subtle)]">
                    {t('withdrawPasswordLabel')}
                  </span>
                  <input
                    type="password"
                    className="input input-bordered input-sm w-full"
                    value={withdrawPassword}
                    onChange={(event) => setWithdrawPassword(event.target.value)}
                    placeholder={t('withdrawPasswordPlaceholder')}
                    autoComplete="current-password"
                  />
                </label>
                <div className="flex items-center justify-end gap-2">
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => {
                      setIsWithdrawConfirmOpen(false);
                      setWithdrawPassword('');
                    }}
                    disabled={withdrawMutation.isPending}
                  >
                    {t('withdrawBack')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-error btn-sm"
                    onClick={handleWithdrawRequest}
                    disabled={withdrawMutation.isPending}
                  >
                    {withdrawMutation.isPending ? t('withdrawSubmitting') : t('withdrawSubmit')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : activeProfileTab === 'notifications' ? (
        <div id="profile-tabpanel-notifications" role="tabpanel" aria-labelledby="profile-tab-notifications">
          <PersonalNotificationRules token={token} />
        </div>
      ) : activeProfileTab === 'sessions' ? (
        <div id="profile-tabpanel-sessions" role="tabpanel" aria-labelledby="profile-tab-sessions">
          <SessionManagement />
        </div>
      ) : (
        <div id="profile-tabpanel-deployDiagnostics" role="tabpanel" aria-labelledby="profile-tab-deployDiagnostics">
          <DeployDiagnosticsPanel />
        </div>
      )}

      <DeleteConfirmModal
        isOpen={isWithdrawFinalConfirmOpen}
        title={t('withdrawFinalConfirmTitle')}
        message={t('withdrawFinalConfirmMessage')}
        confirmLabel={t('withdrawSubmit')}
        cancelLabel={t('withdrawBack')}
        isPending={withdrawMutation.isPending}
        onConfirm={() => void handleWithdraw()}
        onCancel={() => setIsWithdrawFinalConfirmOpen(false)}
      />
    </BaseModal>
  );
}
